import { defineStore } from 'pinia';
import { ref } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';

// Front-only appointment state shared by every zone-mounted app (agenda, appointment card, route
// optimizer). The back-end doesn't persist it, so it lives in localStorage — one entry per customer
// and user, shaped as { pinned: {...}, <future slices> } so new slices can be added without migration.
//
// pinned: { [appointmentUid]: 'YYYY-MM-DD' } — visits that keep their slot during route optimization
// (#23500 RG-22). The date is the visit day, used to purge pins once the day is over; the legacy agenda
// keeps it up to date when a pinned visit is moved (followPin).

const STORAGE_PREFIX = 'appointmentStore';

// Read on every pin lookup (once per rendered agenda event): raw iframe values, no deep clone of the user.
function storageKey() {
	const ctx = iframeDataBridge.getIframeWindow()?.externis?.ctx;
	return `${STORAGE_PREFIX}.${ctx?.customer?.reference ?? 'unknown'}.${ctx?.user?.id ?? 'unknown'}`;
}

const todayKey = () => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function readStorage(key) {
	try {
		const raw = localStorage.getItem(key);
		const data = raw ? JSON.parse(raw) : {};
		return data && typeof data === 'object' ? data : {};
	} catch {
		return {};
	}
}

function writeStorage(key, data) {
	try {
		localStorage.setItem(key, JSON.stringify(data));
	} catch {
		// ignore storage errors (e.g. private browsing)
	}
}

const purgePastPins = (pinned) => {
	const today = todayKey();
	return Object.fromEntries(Object.entries(pinned || {}).filter(([uid, date]) => uid && typeof date === 'string' && date >= today));
};

export const useAppointmentStore = defineStore('appointment', () => {
	const pinned = ref({});
	let loadedKey = null;

	// The key depends on the connected user, so state is (re)loaded lazily on first access per user.
	function ensureLoaded() {
		const key = storageKey();
		if (key === loadedKey) return key;
		loadedKey = key;
		pinned.value = purgePastPins(readStorage(key).pinned);
		return key;
	}

	function persist() {
		const key = ensureLoaded();
		writeStorage(key, { ...readStorage(key), pinned: pinned.value });
	}

	function isPinned(uid) {
		ensureLoaded();
		return !!uid && !!pinned.value[uid];
	}

	function setPinned(uid, date, value) {
		if (!uid) return;
		ensureLoaded();
		const next = { ...pinned.value };
		if (value && date) next[uid] = date;
		else delete next[uid];
		pinned.value = next;
		persist();
	}

	// Moves the purge date of a pinned visit to its current day, so a pin moved to a later day isn't dropped early.
	function followPin(uid, date) {
		if (!date || !isPinned(uid) || pinned.value[uid] === date) return;
		setPinned(uid, date, true);
	}

	function pinnedUids() {
		ensureLoaded();
		return new Set(Object.keys(pinned.value));
	}

	ensureLoaded();

	return { pinned, isPinned, setPinned, followPin, pinnedUids };
});
